
import _ from 'underscore';
import { useMemo, useCallback } from 'react';
import { usePostABTestingFlightLog as useOverreactPostABTestingFlightLog } from '@bingads-webui/overreact-odata-campaign';

// Please refer to wiki here: https://msasg.visualstudio.com/DefaultCollection/Bing_Ads/_wiki/wikis/Bing_Ads.wiki/80390/Flight-Event-APIs-for-AB-Testing-Logging

import { getDescriptorIds, postponeRead, getSharedValues, useOnceEffect } from './ab-testing-event-log';

export const toArray = args => (_.isArray(args) ? args : [args]);

export function usePostABTestingFlightLog(serverContext, logTrace) {
  const accountLevelVariables = useMemo(() => {
    const descriptor = getDescriptorIds(serverContext);

    return {
      locator: {
        descriptor,
      },
    };
  }, [serverContext]);

  const [, { callFn: postABTestingFlightLog }] =
    useOverreactPostABTestingFlightLog(accountLevelVariables, postponeRead);

  return useCallback((args) => {
    const dataList = toArray(args);
    const data = _.map(dataList, ({
      UniqueId,
      RandomizationIdType,
      RandomizationId,
      ExperimentName,
      Flight,
    }) => {
      const flight = {
        UniqueId,
        RandomizationIdType,
        RandomizationId,
        ExperimentName,
        Flight,
      };
      logTrace(`[PostABTestingFlightLog]: ${JSON.stringify(flight)}`);
      return flight;
    });

    postABTestingFlightLog({
      payload: { data },
    });
  }, [logTrace, postABTestingFlightLog]);
}

export function usePostPageIdUserNameFlight(serverContext, logTrace) {
  const postABTestingFlightLog = usePostABTestingFlightLog(serverContext, logTrace);

  const { SessionId, PageTrackingId } = getSharedValues(serverContext);


  return useCallback((args) => {
    // filter out shouldNotPost
    const flights = toArray(args);
    const data = _.compact(_.map(flights, (value) => {
      if (value && value.ExperimentName && value.Flight) {
        const { ExperimentName, Flight } = value;
        return {
          UniqueId: SessionId,
          RandomizationIdType: 'Unknown',
          RandomizationId: PageTrackingId,
          ExperimentName: `${ExperimentName}PageId`,
          Flight,
        };
      }
      return null;
    }));
    // compact data
    // then only post if there is data
    if (!_.isEmpty(data)) {
      postABTestingFlightLog(data);
    }
  }, [PageTrackingId, SessionId, postABTestingFlightLog]);
}

export function usePostHashedUserNameFlight(serverContext, logTrace) {
  const postABTestingFlightLog = usePostABTestingFlightLog(serverContext, logTrace);

  const { SessionId, HashedUserName } = getSharedValues(serverContext);


  return useCallback(({ ExperimentName, Flight }) => {
    const data = [{
      UniqueId: SessionId,
      RandomizationIdType: 'HashedUserName',
      RandomizationId: HashedUserName,
      ExperimentName,
      Flight,
    }];

    postABTestingFlightLog(data);
  }, [HashedUserName, SessionId, postABTestingFlightLog]);
}

export function usePostABTestingFlightLogOnce({
  serverContext,
  logTrace,
  ExperimentName,
  Flight,
}) {
  const postHashedUserNameFlight = usePostHashedUserNameFlight(serverContext, logTrace);

  useOnceEffect(() => {
    if (ExperimentName && Flight) {
      postHashedUserNameFlight({ ExperimentName, Flight });
      return true;
    }
    return false;
  });
}
